<template>
  <div class="listing-search-wrap">
    <template v-if="isMobile">
      <button ref="trigger" type="button" class="listing-search-trigger" @click="sheetOpen = true">
        <SvgIcon name="search" :size="20" />
        <span class="listing-search-trigger__txt">
          <span class="listing-search-trigger__main">{{ locationLabel }}</span>
          <span class="listing-search-trigger__sub">{{ priceLabel }} · {{ housingLabel }}</span>
        </span>
      </button>
      <SearchFilterSheet
        v-if="sheetOpen"
        :api="api"
        :bootstrap="bootstrap"
        :price-label="priceLabel"
        :housing-label="housingLabel"
        @close="closeSheet"
        @reset="resetAll"
        @search="submit"
        @pick-place="onPickPlace"
        @pick-project="onPickProject"
      />
    </template>
    <div v-if="!isMobile && openSegment" class="listing-search__scrim" @mousedown="close" />
    <div v-if="!isMobile" ref="barEl" class="listing-search" :class="{ 'is-active': openSegment }">
      <div
        :ref="(el) => (segmentEls.location = el)"
        class="listing-search__segment listing-search__segment--location"
        :class="{ 'is-open': openSegment === 'location' }"
        @mousedown="onLocationMousedown"
      >
        <span class="listing-search__segment-label">{{ t('listing-search.location.label') }}</span>
        <input
          class="listing-search__segment-input"
          maxlength="75"
          :placeholder="t('listing-search.location.placeholder')"
          :value="locationInputValue"
          @focus="show('location')"
          @input="onLocationInput($event.target.value)"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="openSegment === 'location'"
          :aria-controls="locationPopover && locationPopover.listboxId"
          :aria-activedescendant="locationPopover && locationPopover.activeId"
          @keydown.down.prevent="locationPopover && locationPopover.moveHighlight(1)"
          @keydown.up.prevent="locationPopover && locationPopover.moveHighlight(-1)"
          @keydown.enter.prevent="locationPopover && locationPopover.pickHighlighted()"
        />
        <button
          v-if="locationFilled"
          type="button"
          class="listing-search__segment-clear"
          :aria-label="t('listing-search.location.clear')"
          @mousedown.stop
          @click.stop="clearLocation"
        >
          <SvgIcon name="x" :size="12" />
        </button>
      </div>

      <span class="listing-search__divider" />

      <div
        :ref="(el) => (segmentEls.price = el)"
        class="listing-search__segment listing-search__segment--price"
        :class="{ 'is-open': openSegment === 'price' }"
        role="button"
        tabindex="0"
        aria-haspopup="dialog"
        :aria-expanded="openSegment === 'price'"
        @mousedown="show('price')"
        @keydown.enter.prevent="show('price')"
        @keydown.space.prevent="show('price')"
      >
        <span class="listing-search__segment-label">{{ t('listing-search.price.label') }}</span>
        <span class="listing-search__segment-value" :class="{ 'is-filled': priceFilled }">
          {{ priceLabel }}
        </span>
        <button
          v-if="priceFilled"
          type="button"
          class="listing-search__segment-clear"
          :aria-label="t('listing-search.price.clear')"
          @mousedown.stop
          @click.stop="update({ pmin: '', pmax: '' })"
        >
          <SvgIcon name="x" :size="12" />
        </button>
      </div>

      <span class="listing-search__divider" />

      <div
        :ref="(el) => (segmentEls.housing = el)"
        class="listing-search__segment listing-search__segment--housing"
        :class="{ 'is-open': openSegment === 'housing' }"
        role="button"
        tabindex="0"
        aria-haspopup="dialog"
        :aria-expanded="openSegment === 'housing'"
        @mousedown="show('housing')"
        @keydown.enter.prevent="onHousingEnter"
        @keydown.space.prevent="show('housing')"
      >
        <span class="listing-search__segment-label">{{ t('listing-search.housing.label') }}</span>
        <span class="listing-search__segment-value" :class="{ 'is-filled': housingFilled }">
          {{ housingLabel }}
        </span>
        <button
          v-if="housingFilled"
          type="button"
          class="listing-search__segment-clear"
          :aria-label="t('listing-search.housing.clear')"
          @mousedown.stop
          @click.stop="clearHousing"
        >
          <SvgIcon name="x" :size="12" />
        </button>
      </div>

      <div v-if="openSegment" class="listing-search__popover" :style="popoverStyle" @mousedown.stop>
        <div :key="openSegment" class="listing-search__popover-inner">
          <PopoverLocation
            v-if="openSegment === 'location'"
            ref="locationPopover"
            :api="api"
            :bootstrap="bootstrap"
            skippable
            @pick-place="onPickPlace"
            @pick-project="onPickProject"
            @next="advance('price')"
          />
          <PopoverPrice v-if="openSegment === 'price'" skippable @next="advance('housing')" />
          <PopoverHousing v-if="openSegment === 'housing'" :bootstrap="bootstrap" />
        </div>
      </div>

      <button ref="submitButton" type="button" class="listing-search__submit" @click="submit">
        <SvgIcon name="search" :size="22" />
        <span class="listing-search__submit-text">{{ t('listing-search.submit') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import PopoverLocation from './PopoverLocation.vue';
import PopoverPrice from './PopoverPrice.vue';
import PopoverHousing from './PopoverHousing.vue';
import SearchFilterSheet from './SearchFilterSheet.vue';
import { useCriteria } from '../lib/useCriteria';
import { t } from '../lib/i18n';
import { BEDROOM_PLUS_FROM, DEFAULT_RADIUS } from '../lib/criteriaContract';

const props = defineProps({
  api: { type: Object, required: true },
  bootstrap: { type: Object, required: true }
});

const emit = defineEmits(['search', 'pick-project']);

const { criteria, update, reset, persist } = useCriteria();

const mobileMediaQuery = window.matchMedia('(max-width: 1024px)');
const isMobile = ref(mobileMediaQuery.matches);
const onMediaChange = (event) => {
  isMobile.value = event.matches;
};

const sheetOpen = ref(false);
const trigger = ref(null);

const openSegment = ref(null);
const barEl = ref(null);
const segmentEls = reactive({ location: null, price: null, housing: null });
const popoverStyle = ref(null);

const POPOVER_MIN_WIDTH = { location: 340, price: 436, housing: 436 };
// The popover opens 1rem below the bar; the margin covers that gap plus
// breathing room above the viewport bottom.
const POPOVER_VIEWPORT_MARGIN = 24;
const POPOVER_MIN_HEIGHT = 160;

const selectedPlace = ref(null);
const locationPopover = ref(null);
const submitButton = ref(null);

const formatEuro = (value) => (value ? `€ ${Number(value).toLocaleString('nl-NL')}` : '');

const locationFilled = computed(() => Boolean(criteria.loc));

// A typed place without coordinates filters nothing (see apiParams), so the
// summary only claims a location once a place with coordinates is picked.
const locationLabel = computed(() => {
  if (criteria.long === null || criteria.lat === null) return t('listing-search.location.anywhere');
  return criteria.radius === null ? criteria.loc : `${criteria.loc} · ${criteria.radius} km`;
});

// While the location popover is open the input holds the raw editable query;
// closed, it doubles as the summary and shows the radius alongside the place.
const locationInputValue = computed(() => {
  if (openSegment.value === 'location') return criteria.loc;
  if (criteria.long === null || criteria.lat === null || criteria.radius === null) {
    return criteria.loc;
  }
  return `${criteria.loc} · ${criteria.radius} km`;
});
const priceFilled = computed(() => Boolean(criteria.pmin || criteria.pmax));

const priceLabel = computed(() => {
  if (!priceFilled.value) return t('listing-search.price.all');
  const min = formatEuro(criteria.pmin);
  const max = formatEuro(criteria.pmax);
  if (min && max) return t('listing-search.price.between', { min, max });
  if (min) return t('listing-search.price.from', { price: min });
  return t('listing-search.price.up-to', { price: max });
});

const housingLabel = computed(() => {
  const parts = [];
  if (criteria.types.length) {
    if (criteria.types.length === 1) {
      const match = props.bootstrap.housingTypes.find((type) => type.slug === criteria.types[0]);
      parts.push(match ? match.label : criteria.types[0]);
    } else {
      parts.push(t('listing-search.housing.types-count', { count: criteria.types.length }));
    }
  }
  if (criteria.m2) {
    const { min, max } = props.bootstrap.bounds.m2;
    const lo = criteria.m2[0] ?? min;
    const hi = criteria.m2[1] ?? max;
    if (lo > min || hi < max) {
      parts.push(`${lo}–${hi}${hi >= max ? '+' : ''} m²`);
    }
  }
  if (criteria.beds.length) {
    const beds = [...criteria.beds]
      .sort()
      .map((bed) => (bed >= BEDROOM_PLUS_FROM ? `${BEDROOM_PLUS_FROM}+` : bed));
    parts.push(t('listing-search.housing.bedrooms-summary', { beds: beds.join(',') }));
  }
  if (criteria.perceel) {
    const { min, max } = props.bootstrap.bounds.perceel;
    const lo = criteria.perceel[0] ?? min;
    const hi = criteria.perceel[1] ?? max;
    if (lo > min || hi < max) {
      parts.push(
        t('listing-search.housing.parcel-summary', {
          range: `${lo}–${hi}${hi >= max ? '+' : ''}`
        })
      );
    }
  }
  return parts.length ? parts.join(' · ') : t('listing-search.housing.all');
});

const housingFilled = computed(() => housingLabel.value !== t('listing-search.housing.all'));

// Every filter change (including clearing) persists immediately, not only on
// submit.
const rangeBounds = computed(() => ({
  m2: [props.bootstrap.bounds.m2.min, props.bootstrap.bounds.m2.max],
  perceel: [props.bootstrap.bounds.perceel.min, props.bootstrap.bounds.perceel.max]
}));

watch(criteria, () => persist(rangeBounds.value), { deep: true });

// The filter bar's "Filters wissen" resets the shared criteria from outside
// this component; drop the local place so a later submit doesn't replay it.
watch(
  () => criteria.long,
  (long) => {
    if (long === null) selectedPlace.value = null;
  }
);

const measurePopover = () => {
  const segment = segmentEls[openSegment.value];
  if (!segment || !barEl.value) return;
  const barWidth = barEl.value.offsetWidth;
  const width = Math.min(
    barWidth,
    Math.max(POPOVER_MIN_WIDTH[openSegment.value] || 360, segment.offsetWidth)
  );
  const left = Math.min(segment.offsetLeft, Math.max(0, barWidth - width));
  const maxHeight = Math.max(
    POPOVER_MIN_HEIGHT,
    window.innerHeight - barEl.value.getBoundingClientRect().bottom - POPOVER_VIEWPORT_MARGIN
  );
  popoverStyle.value = { left: `${left}px`, width: `${width}px`, maxHeight: `${maxHeight}px` };
};

watch(openSegment, async (segment) => {
  if (!segment) {
    popoverStyle.value = null;
    return;
  }
  await nextTick();
  measurePopover();
});

// Opening the segment should land focus in the input right away; preventDefault
// stops the browser from stealing focus back on mousedown (except on the input
// itself, where the default click places the cursor).
const onLocationMousedown = (event) => {
  if (!event.target.closest('.listing-search__segment-input')) event.preventDefault();
  show('location');
};

const show = async (segment) => {
  openSegment.value = segment;
  if (segment === 'location') {
    await nextTick();
    const input = segmentEls.location
      ? segmentEls.location.querySelector('.listing-search__segment-input')
      : null;
    if (input && document.activeElement !== input) input.focus();
  }
};

const close = () => {
  openSegment.value = null;
};

// Stepping on with Enter carries focus along, so the next Enter acts on the
// step that just opened instead of on the location input it left behind.
const advance = async (segment) => {
  openSegment.value = segment;
  await nextTick();
  const target =
    segment === 'price' ? barEl.value.querySelector('.listing-search__popover input') : segmentEls[segment];
  if (target) target.focus();
};

// The housing step has no input of its own, so its segment holds focus while
// open; Enter there ends the walk on the search button rather than searching
// right away, which keeps searching without a place a deliberate choice.
const onHousingEnter = () => {
  if (openSegment.value !== 'housing') {
    show('housing');
    return;
  }
  close();
  submitButton.value.focus();
};

const onEscape = () => {
  const segment = openSegment.value;
  close();
  if (segment && segmentEls[segment]) {
    const el =
      segment === 'location'
        ? segmentEls.location.querySelector('.listing-search__segment-input')
        : segmentEls[segment];
    if (el) el.focus();
  }
};

const onLocationInput = (value) => {
  selectedPlace.value = null;
  update({ loc: value, long: null, lat: null });
};

const clearLocation = () => {
  selectedPlace.value = null;
  update({ loc: '', long: null, lat: null, radius: DEFAULT_RADIUS });
};

const clearHousing = () => update({ m2: null, perceel: null, beds: [], types: [] });

const closeSheet = () => {
  sheetOpen.value = false;
  if (trigger.value) trigger.value.focus();
};

const resetAll = () => {
  selectedPlace.value = null;
  reset();
};

const onPickPlace = (place) => {
  selectedPlace.value = place;
};

const onPickProject = (project) => {
  close();
  emit('pick-project', project);
};

const submit = () => {
  close();
  sheetOpen.value = false;
  emit('search', { place: selectedPlace.value });
};

const onDocMousedown = (event) => {
  if (barEl.value && !barEl.value.contains(event.target)) close();
};

const onDocKeydown = (event) => {
  if (event.key === 'Escape' && openSegment.value) onEscape();
};

const onResize = () => {
  if (openSegment.value) measurePopover();
};

onMounted(() => {
  document.addEventListener('mousedown', onDocMousedown);
  document.addEventListener('keydown', onDocKeydown);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onResize, { passive: true });
  mobileMediaQuery.addEventListener('change', onMediaChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMousedown);
  document.removeEventListener('keydown', onDocKeydown);
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onResize);
  mobileMediaQuery.removeEventListener('change', onMediaChange);
});
</script>
